import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';

const API = '/technical-forms';

const STATUS_OPTIONS = ['Draft', 'In progress', 'Completed', 'Closed'];
const STATUS_COLORS = {
  'Draft': '#6b7280',
  'In progress': '#2563eb',
  'Completed': '#16a34a',
  'Closed': '#991b1b',
};

const FormFill = ({ formId, projectId, onBack, showToast }) => {
  const { instance, accounts } = useMsal();
  const [form, setForm] = useState(null);
  const [templateFields, setTemplateFields] = useState([]);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Editable metadata
  const [formMeta, setFormMeta] = useState({
    form_name: '', description: '', form_date: '', status: 'In progress',
    location: '', assigned_to: '', due_date: '',
  });

  const fetchForm = useCallback(async () => {
    try {
      setLoading(true);
      const id = typeof formId === 'object' ? formId.id : formId;
      const res = await authenticatedApiCall(instance, accounts, 'GET', `${API}/forms/${id}`);
      if (res.data.success) {
        const f = res.data.data;
        setForm(f);
        setResponses(f.responses || {});
        setTemplateFields(f.template_fields || []);
        setFormMeta({
          form_name: f.form_name || '',
          description: f.description || '',
          form_date: f.form_date ? f.form_date.split('T')[0] : '',
          status: f.status || 'In progress',
          location: f.location || '',
          assigned_to: f.assigned_to || '',
          due_date: f.due_date ? f.due_date.split('T')[0] : '',
        });
      }
    } catch (err) {
      console.error('Fetch form error:', err);
      showToast?.('Failed to load form', 'error');
    } finally {
      setLoading(false);
    }
  }, [formId, instance, accounts, showToast]);

  useEffect(() => { fetchForm(); }, [fetchForm]);

  const handleResponseChange = (fieldId, value) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));
    setDirty(true);
  };

  const handleMetaChange = (key, value) => {
    setFormMeta(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const id = typeof formId === 'object' ? formId.id : formId;
      await authenticatedApiCall(instance, accounts, 'PUT', `${API}/forms/${id}`, {
        ...formMeta,
        responses,
      });
      showToast?.('Form saved successfully');
      setDirty(false);
    } catch (err) {
      console.error('Save form error:', err);
      showToast?.('Failed to save form', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
        <div style={{ width: 32, height: 32, border: '3px solid #e5e7eb', borderTop: '3px solid #2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
        Loading form...
        <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!form) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#ef4444' }}>
        Form not found.
        <br />
        <button onClick={onBack} style={{ marginTop: 12, padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>Go Back</button>
      </div>
    );
  }

  const inputStyle = {
    width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6,
    fontSize: 14, outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)' }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px',
        borderBottom: '1px solid #e5e7eb', backgroundColor: '#fff',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onBack} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, color: '#6b7280' }}>←</button>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>
              {formMeta.form_name || `Form #${form.form_number}`}
            </h3>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>
              #{form.form_number}
              {form.template_name && <> · Template: {form.template_name}</>}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {dirty && <span style={{ fontSize: 12, color: '#f59e0b', fontWeight: 500 }}>Unsaved changes</span>}
          <select
            value={formMeta.status}
            onChange={(e) => handleMetaChange('status', e.target.value)}
            style={{
              padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13,
              color: STATUS_COLORS[formMeta.status] || '#374151', fontWeight: 600,
            }}
          >
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={onBack} style={{ padding: '7px 14px', border: '1px solid #d1d5db', borderRadius: 4, backgroundColor: '#fff', cursor: 'pointer', fontSize: 13 }}>
            Close
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '7px 16px', backgroundColor: '#2563eb', color: '#fff', border: 'none',
              borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Form body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 24, backgroundColor: '#f5f7fa' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          {/* Metadata card */}
          <div style={{
            backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
            padding: 20, marginBottom: 20,
          }}>
            <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: '#374151' }}>Form Details</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <label style={{ gridColumn: '1 / -1', fontSize: 13, fontWeight: 600, color: '#374151' }}>
                Form Name
                <input type="text" value={formMeta.form_name} onChange={(e) => handleMetaChange('form_name', e.target.value)} style={{ ...inputStyle, marginTop: 4 }} />
              </label>
              <label style={{ gridColumn: '1 / -1', fontSize: 13, fontWeight: 600, color: '#374151' }}>
                Description
                <textarea value={formMeta.description} onChange={(e) => handleMetaChange('description', e.target.value)} rows={2}
                  style={{ ...inputStyle, marginTop: 4, resize: 'vertical' }} />
              </label>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                Form Date
                <input type="date" value={formMeta.form_date} onChange={(e) => handleMetaChange('form_date', e.target.value)} style={{ ...inputStyle, marginTop: 4 }} />
              </label>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                Due Date
                <input type="date" value={formMeta.due_date} onChange={(e) => handleMetaChange('due_date', e.target.value)} style={{ ...inputStyle, marginTop: 4 }} />
              </label>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                Location
                <input type="text" value={formMeta.location} onChange={(e) => handleMetaChange('location', e.target.value)} style={{ ...inputStyle, marginTop: 4 }} />
              </label>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                Assigned to
                <input type="text" value={formMeta.assigned_to} onChange={(e) => handleMetaChange('assigned_to', e.target.value)} style={{ ...inputStyle, marginTop: 4 }} />
              </label>
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: '#9ca3af' }}>
              Created by: {form.created_by} · Last updated by: {form.last_updated_by}
            </div>
          </div>

          {/* Template fields */}
          {templateFields.length > 0 ? (
            <div style={{
              backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20,
            }}>
              <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: '#374151' }}>
                {form.template_name || 'Form Fields'}
              </h4>
              {templateFields.map((field, idx) => (
                <div key={field.id || idx} style={{ marginBottom: field.type === 'section' ? 8 : 16 }}>
                  {field.type === 'section' ? (
                    <div style={{
                      fontSize: 14, fontWeight: 700, color: '#374151', borderBottom: '2px solid #e5e7eb',
                      paddingBottom: 6, paddingTop: idx > 0 ? 12 : 0,
                    }}>
                      {field.label}
                    </div>
                  ) : (
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                      {field.label}
                      {field.required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
                      <div style={{ marginTop: 4 }}>
                        {renderFieldInput(field, responses[field.id], (v) => handleResponseChange(field.id, v), inputStyle)}
                      </div>
                    </label>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
              padding: 40, textAlign: 'center', color: '#9ca3af',
            }}>
              This form was created without a template. Use the metadata fields above to track information.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const renderFieldInput = (field, value, onChange, inputStyle) => {
  switch (field.type) {
    case 'text':
      return (
        <input
          type="text" value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ''}
          style={inputStyle}
        />
      );
    case 'textarea':
      return (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      );
    case 'number':
      return (
        <input
          type="number" value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...inputStyle, width: 160 }}
        />
      );
    case 'date':
      return (
        <input
          type="date" value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...inputStyle, width: 200 }}
        />
      );
    case 'select':
      return (
        <select value={value || ''} onChange={(e) => onChange(e.target.value)} style={{ ...inputStyle, width: 240 }}>
          <option value="">Select...</option>
          {(field.options || []).map((o, i) => <option key={i} value={o}>{o}</option>)}
        </select>
      );
    case 'checkbox':
      return (
        <div style={{ marginTop: 4 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
            <input
              type="checkbox"
              checked={value || false}
              onChange={(e) => onChange(e.target.checked)}
              style={{ width: 16, height: 16 }}
            />
            <span style={{ color: '#374151' }}>{value ? 'Yes' : 'No'}</span>
          </label>
        </div>
      );
    case 'radio':
      return (
        <div style={{ display: 'flex', gap: 16, marginTop: 4, flexWrap: 'wrap' }}>
          {(field.options || []).map((o, i) => (
            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
              <input
                type="radio"
                name={field.id}
                checked={value === o}
                onChange={() => onChange(o)}
              />
              {o}
            </label>
          ))}
        </div>
      );
    default:
      return <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} style={inputStyle} />;
  }
};

export default FormFill;
